import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useTranslation } from "react-i18next";

import { verifyCertificate } from "@/services/certificateService";
import { ROUTE } from "@/route/app_routes.js";
import CertificateDocument from "./CertificateDocument";
import CertificateShareRow from "./CertificateShareRow";

// recharts (inside CourseCompletionSummary) is a sizable dependency -- dynamic-importing
// keeps it out of this public verify page's initial JS payload (the most-visited unauthenticated
// surface this component appears on, via QR codes/shared links), same reasoning as
// StudentCourseCongratulations.js's own identical import.
const CourseCompletionSummary = dynamic(() => import("./CourseCompletionSummary"), { ssr: false });

/**
 * The page anyone lands on when they open a certificate's verify link -- from the QR code on
 * the PDF/web certificate, or from a link a student shared on social media (see
 * StudentCourseCongratulations.js's own comment: shared links always point here, never at the
 * student's private dashboard view, so everyone who opens the link sees the same thing
 * regardless of who shared it). Shows the certificate itself plus the full Course Completion
 * Summary below it -- module-by-module scores, retakes, time saved -- so someone checking a
 * claimed credential (an employer, say) can see the substance behind the grade, not just the
 * grade.
 *
 * `initialCertificate` comes from the page's own getServerSideProps (pages/certificate/verify/
 * [number].js) when a number is present in the URL -- fetched server-side specifically so the
 * page's Open Graph/Twitter meta tags are already correct in the very first response a social
 * media crawler sees (those bots don't run this component's client-side fetch).
 */
const CertificateVerify = ({ certificateNumber, initialCertificate = null }) => {
  const { t } = useTranslation("certificate");
  const [result, setResult] = useState(initialCertificate);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(Boolean(certificateNumber) && !initialCertificate);
  const [input, setInput] = useState(certificateNumber || "");
  // window.location.origin can't be known during SSR (this page is fetched via
  // getServerSideProps specifically so social crawlers see correct OG tags -- see this file's
  // own doc comment), so it's set here after mount rather than computed inline during render.
  // That distinction actually matters: computing it inline renders "" server-side and the right
  // value client-side, but since nothing else here re-renders after hydration when
  // initialCertificate is already present, React's hydration reconciliation was leaving the
  // share links' href attributes stuck at their empty SSR value indefinitely (confirmed via a
  // "Prop `href` did not match" hydration warning whose logged "Client" value was already
  // correct -- the DOM attribute itself just never got patched to match). Setting it via
  // useEffect forces a genuine post-mount re-render/commit instead of relying on hydration to
  // reconcile a mismatched attribute.
  const [origin, setOrigin] = useState("");
  const router = useRouter();

  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);

  useEffect(() => {
    if (!certificateNumber || initialCertificate) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    verifyCertificate(certificateNumber)
      .then((res) => setResult(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificateNumber]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    router.push(`${ROUTE.certificateVerify}${input.trim()}`);
  };

  const verifyUrl = result && origin ? `${origin}${ROUTE.certificateVerify}${result.certificate_number}` : "";
  const shareText = result ? t("verify.shareText", "Check out this verified certificate from SmartTech Academy: {{course}}", { course: result.course_title }) : "";

  return (
    <>
      <div className="rbt-verify-hero">
        <div className="rbt-verify-hero__glow" aria-hidden="true" />
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <span className="rbt-verify-hero__icon">
                <i className="feather-shield"></i>
              </span>
              <h2 className="rbt-verify-hero__title">{t("verify.title", "Verify a Certificate")}</h2>
              <p className="rbt-verify-hero__description">
                {t("verify.description", "Enter a SmartTech Academy certificate number to confirm it's real.")}
              </p>

              <form onSubmit={handleSubmit} className="rbt-verify-search">
                <i className="feather-hash rbt-verify-search__prefix" aria-hidden="true"></i>
                <input
                  type="text"
                  placeholder={t("verify.placeholder", "e.g. STA-XXXXXXXXXX")}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  autoComplete="off"
                  spellCheck="false"
                />
                <button type="submit" className="rbt-verify-search__btn">
                  <i className="feather-search"></i>
                  <span>{t("verify.checkButton", "Verify")}</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="container ptb--120">
        <div className="row justify-content-center">
          <div className={result ? "col-lg-10 text-center" : "col-lg-6 text-center"}>
            {loading && <p>{t("verify.checking", "Checking…")}</p>}

            {!loading && (notFound || (certificateNumber && !result)) && (
              <div className="rbt-badge-5 bg-color-danger-opacity color-danger">
                {t("verify.notFound", "No certificate found with that number.")}
              </div>
            )}

            {!loading && result && (
              <>
                <div className="rbt-badge-5 bg-color-success-opacity color-success mb--30">
                  <i className="feather-check-circle"></i> {t("verify.verified", "Certificate Verified")}
                </div>
                <CertificateDocument certificate={result} />

                <CourseCompletionSummary certificate={result} />

                <CertificateShareRow url={verifyUrl} text={shareText} label={t("share.verifyLabel", "Share this certificate")} certificate={result} />
              </>
            )}

            <div className="mt--40">
              <Link className="rbt-btn-link" href={ROUTE.course}>
                Browse Free Courses <i className="feather-arrow-right"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CertificateVerify;
